import { useState, useCallback } from "react";
import { notificationService } from "../utils/notificationService";
import { api } from "../utils/apiHandler";

export const useNotifications = () => {
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);

  // Fetch unread notifications from the server
  const fetchUnreadNotifications = useCallback(async () => {
    try {
      // First check unread count for efficiency
      const countResponse = await api.get("/notifications/unread_count");
      const unreadCount = countResponse.data?.unread_count || 0;
      setHasUnreadNotifications(unreadCount > 0);

      if (unreadCount > 0) {
        // Only fetch full notifications if there are unread ones
        const response = await api.get("/notifications");
        const allNotifications = response.data || [];
        // Filter for unread notifications (where read_at is null)
        const unreadNotifications = allNotifications.filter(
          notification => !notification.read_at
        );
        setNotifications(unreadNotifications);
        return unreadNotifications;
      } else {
        setNotifications([]);
        return [];
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      return [];
    }
  }, [setNotifications]);

  // Mark notifications as read
  const markAsRead = useCallback(
    async (notificationIds = []) => {
      try {
        if (notificationIds.length === 0) {
          // Mark all notifications as read
          await api.post("/notifications/mark_all_as_read");
          setNotifications([]);
          setHasUnreadNotifications(false);
        } else {
          // Mark specific notifications as read
          for (const notificationId of notificationIds) {
            await api.post(`/notifications/${notificationId}/mark_as_read`);
          }

          // Update local state
          setNotifications(prev =>
            prev.filter(
              notification => !notificationIds.includes(notification.id)
            )
          );

          // Check if there are still unread notifications
          setNotifications(prev => {
            const remainingUnread = prev.filter(
              n => !notificationIds.includes(n.id)
            );
            setHasUnreadNotifications(remainingUnread.length > 0);
            return remainingUnread;
          });
        }
      } catch (error) {
        console.error("Failed to mark notifications as read:", error);
      }
    },
    [setNotifications]
  );

  // Initialize WebSocket connection
  const initializeNotifications = useCallback(
    (userId, accessToken) => {
      // Set up notification service callbacks
      notificationService.onNotification(notification => {
        setNotifications(prev => [notification, ...prev]);
        setHasUnreadNotifications(true);
      });

      notificationService.onConnect(() => {
        setIsConnected(true);
      });

      notificationService.onDisconnect(() => {
        setIsConnected(false);
      });

      // Connect to the notification service
      notificationService.connect(userId, accessToken);

      // Fetch existing unread notifications
      fetchUnreadNotifications();
    },
    [fetchUnreadNotifications, setNotifications]
  );

  // Clean up on unmount
  const cleanup = useCallback(() => {
    notificationService.disconnect();
    setIsConnected(false);
  }, []);

  // Manual reconnection function
  const reconnect = useCallback(() => {
    notificationService.reconnect();
  }, []);

  return {
    hasUnreadNotifications,
    notifications,
    isConnected,
    initializeNotifications,
    markAsRead,
    cleanup,
    reconnect,
  };
};
